import { EntityCommand, EntityCommandTypeEnum } from "./EntityCommand";
import { EntityCommandInformation } from "./EntityCommandInformation";
import { Nullable, PartialNullable } from "./Extensions";
import { ArrayHelper } from "./Helpers/ArrayHelper";
import { StringHelper } from "./Helpers/StringHelper";
import { NAMESPACE } from "./_Namespace";
import { IRadObject, Property, RadObject, RadObjectPropertyValues } from "./RadObject";
import { AndEvaluator } from "./Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/AndEvaluator";
import { PermissionEvaluator } from "./Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { Evaluator } from "./Server/DotNet/SeriouslyRAD.Framework/Evaluation/Evaluator";
import { Menu, MenuItem } from "./Ui/Controls/Menu";

export type EntityManagerPropertyValues =
    RadObjectPropertyValues
    & {
    };

export type EntityManagerProperties = {
    [name in keyof EntityManagerPropertyValues]: Property<EntityManagerPropertyValues[name]>
};

export interface IEntityManager extends IRadObject, EntityManagerProperties
{
}

export class EntityManager extends RadObject<EntityManagerPropertyValues> implements IEntityManager
{
    public static override classNamespace = NAMESPACE + "EntityManager";
    public static override className = "EntityManager";

    // #region Instance

    // #region Global Variables

    protected entityCommands: EntityCommand[];
    protected removedEntityCommands: { entityName: string, key: string, type: EntityCommandTypeEnum }[];
    protected menu: Nullable<Menu>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntityManagerPropertyValues>)
    {
        super(propertyValues);

        this.entityCommands = [];
        this.removedEntityCommands = [];
        this.menu = null;
    }

    // #endregion

    // #region Public Methods

    public cloneEntityCommands(entityCommands: EntityCommand[]): EntityCommand[]
    {
        return entityCommands.map(item => item.clone());
    }
    public createCommandMenu(entityName: string, commands: EntityCommand[])
    {
        if (!commands.length)
            return null;

        const
            menu = new Menu({ text: "Actions" });

        ArrayHelper
            .orderBy(commands, item => StringHelper.format("{0}_{1}", item.isDefault.get() ? "A" : "B", item.description.get()))
            .forEach(item =>
            {
                const
                    menuItem = new MenuItem({
                        text: item.description.get(),
                        toggle: !!item.isToggle.get(),
                        tag: {
                            entityCommand: item
                        }
                    });

                const
                    visibleEvaluator = item.visibleEvaluator.get(),
                    permission = item.permission.get(),
                    combinedVisibleEvaluator = new AndEvaluator();

                if (visibleEvaluator)
                    combinedVisibleEvaluator.addEvaluator(visibleEvaluator.clone());

                if (permission)
                    combinedVisibleEvaluator.addEvaluator(new PermissionEvaluator({ entityName, permission }));

                if (combinedVisibleEvaluator.evaluators.get()?.length)
                    menuItem.visibleEvaluator.set(combinedVisibleEvaluator);

                menu.append(menuItem);
            });

        return menu;
    }
    public createEntityUrl(entityCommandInformation: EntityCommandInformation)
    {
        return entityCommandInformation ? entityCommandInformation.createEntityUrl() : null;
    }
    //public executeCommand(entityCommandInformation: EntityCommandInformation)
    //{
    //    const
    //        entityCommand = this.getEntityCommand(commandInformation.typeGet(), commandInformation.entityNameGet(), commandInformation.keyGet());

    //    if (!entityCommand)
    //        return;

    //    entityCommand.executeAsync(entityCommandInformation);
    //}
    public getEntityCommand(type: string, entityName: string, key: string): Nullable<EntityCommand>
    {
        const
            entityCommand =
                ArrayHelper.firstOrDefault(
                    ArrayHelper.orderBy(
                        this.entityCommands
                            .filter(item =>
                            {
                                const
                                    itemEntityName = item.entityName.get();

                                return item.type.get() === type && (itemEntityName === undefined || itemEntityName === entityName) && (key === undefined && item.isDefault.get() || item.key.get() === key);
                            }),
                        item =>
                        {
                            return StringHelper.format("{0}{1}", item.isDefault.get() ? "0" : "1", item.entityName.get() !== undefined ? "0" : "1");
                        }
                    )
                );

        return entityCommand ? entityCommand.clone() : null;
    }
    public getEntityCommands(type: string, entityName: string, owner?: any)
    {
        const
            commands: EntityCommand[] = [];

        // Add entity-specific commands
        commands.push(
            ...this.entityCommands
                .filter(item =>
                {
                    return (
                        item.type.get() === type
                        && item.entityName.get() === entityName
                    );
                })
        );

        // Add global commands
        commands.push(
            ...this.entityCommands
                .filter(item =>
                {
                    const
                        itemKey = item.key.get();

                    return (
                        item.type.get() === type
                        && !item.entityName.get()
                        && !commands.some(itemInner => itemInner.key.get() === itemKey)
                    );
                })
        );

        // Exclude removed commands
        ArrayHelper.removeWhere(
            commands,
            item =>
            {
                return (
                    this.removedEntityCommands.some(itemInner =>
                    {
                        return (
                            (!itemInner.entityName || itemInner.entityName === entityName)
                            && itemInner.key === item.key.get()
                            && itemInner.type === item.type.get()
                        );
                    })
                );
            }
        );

        return (
            this.cloneEntityCommands(ArrayHelper.orderBy(commands, item => item.description.get()))
                .map(item =>
                {
                    if (owner)
                        item.owner.set(owner);

                    return item;
                })
        );
    }
    public registerCommand(command: EntityCommand)
    {
        if (command.isDefault.get())
        {
            const
                commandEntityName = command.entityName.get(),
                commandType = command.type.get();

            this.entityCommands
                .filter(item =>
                {
                    return (
                        item.type.get() === commandType
                        && item.entityName.get() === commandEntityName
                        && item.isDefault.get()
                    );
                })
                .forEach(item => { item.isDefault.set(false); });
        }

        this.entityCommands.push(command);
    }
    public removeCommand(command: { entityName: string, key: string, type: EntityCommandTypeEnum })
    {
        this.removedEntityCommands.push(command);
    }

    // #endregion

    // #endregion
}

